import React, { useEffect, useRef, useState } from "react";
import { Check, X, UserMinus } from "lucide-react";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../../components/layout/view/ViewHeader";
import EmptyState from "../../../../../components/feedback/EmptyState";
import { EllipsisMenu } from "../../../../../components/menu/ellipsis/EllipsisMenu";

import { useCreatorGroups } from "../../hooks/useCreatorGroups";

import "./GroupCreatorMembers.css";

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Request failed.";

const getStatus = (member) =>
  String(member?.status || "")
    .trim()
    .toLowerCase();

const getUsername = (member) =>
  String(member?.username || member?.fullName || "").trim() ||
  `User ${member?.userId || ""}`;

const FILTERS = ["all", "approved", "pending"];

export default function GroupCreatorMembers({ username, groupTag }) {
  const {
    members,
    memberCounts,
    membersLoading,
    membersError,
    getCreatorGroupMembers,
    updateCreatorGroupMemberStatus,
    removeCreatorGroupMember,
  } = useCreatorGroups(username);

  const fetchRef = useRef(getCreatorGroupMembers);
  fetchRef.current = getCreatorGroupMembers;

  const [statusFilter, setStatusFilter] = useState("all");
  const [workingMemberId, setWorkingMemberId] = useState(null);
  const [actionError, setActionError] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;

    setStatusFilter("all");
    setActionError("");
    setLoaded(false);

    if (!username || !groupTag) return;

    const loadMembers = async () => {
      try {
        await fetchRef.current(groupTag, "all");
      } catch {
        // Errors are handled by the hook.
      } finally {
        if (active) setLoaded(true);
      }
    };

    void loadMembers();

    return () => {
      active = false;
    };
  }, [username, groupTag]);

  const list = Array.isArray(members) ? members : [];

  const counts = {
    total: Number(memberCounts?.total ?? list.length),
    approved: Number(memberCounts?.approved ?? 0),
    pending: Number(memberCounts?.pending ?? 0),
  };

  const filteredMembers =
    statusFilter === "all"
      ? list
      : list.filter((member) => getStatus(member) === statusFilter);

  const handleRefresh = async () => {
    if (membersLoading || workingMemberId !== null) return;

    setActionError("");

    try {
      await getCreatorGroupMembers(groupTag, "all");
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  };

  const handleChangeStatus = async (memberId, status) => {
    if (workingMemberId !== null) return;

    const action = status === "Approved" ? "Approve" : "Decline";

    if (!window.confirm(`${action} this membership request?`)) {
      return;
    }

    setWorkingMemberId(memberId);
    setActionError("");

    try {
      await updateCreatorGroupMemberStatus(groupTag, memberId, status);
    } catch (error) {
      setActionError(getErrorMessage(error));
    } finally {
      setWorkingMemberId(null);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (workingMemberId !== null) return;

    if (!window.confirm("Remove this member from the group?")) {
      return;
    }

    setWorkingMemberId(memberId);
    setActionError("");

    try {
      await removeCreatorGroupMember(groupTag, memberId);
    } catch (error) {
      setActionError(getErrorMessage(error));
    } finally {
      setWorkingMemberId(null);
    }
  };

  console.log(members);

  return (
    <ViewContainer>
      <ViewHeader
        title={`Group Members (${counts.approved})`}
        description={`${counts.pending} pending request${
          counts.pending === 1 ? "" : "s"
        }. Review requests and manage members.`}
      />

      <div className="creator-owner-group-members__filters">
        {FILTERS.map((filter) => (
          <button
            key={filter}
            type="button"
            className={statusFilter === filter ? "is-active" : ""}
            aria-pressed={statusFilter === filter}
            onClick={() => setStatusFilter(filter)}
          >
            {filter === "all"
              ? `All (${counts.total})`
              : filter === "approved"
                ? `Approved (${counts.approved})`
                : `Pending (${counts.pending})`}
          </button>
        ))}
      </div>

      {(actionError || membersError) && (
        <p className="creator-owner-group-members__error" role="alert">
          {actionError || membersError}
        </p>
      )}

      {!loaded || (membersLoading && !list.length) ? (
        <p className="creator-owner-group-members__loading" role="status">
          Loading members...
        </p>
      ) : filteredMembers.length === 0 ? (
        <EmptyState
          title={
            statusFilter === "pending"
              ? "No pending requests"
              : statusFilter === "approved"
                ? "No approved members"
                : "No members found"
          }
          description={
            statusFilter === "pending"
              ? "New membership requests will appear here."
              : "Members who request access will appear here."
          }
        />
      ) : (
        <div className="creator-owner-group-members__list">
          {filteredMembers.map((member) => {
            const memberId = Number(member.memberId);
            const status = getStatus(member);
            const name = getUsername(member);

            const options = [
              ...(status === "pending"
                ? [
                    {
                      label: "Approve Request",
                      icon: <Check size={14} />,
                      action: () => handleChangeStatus(memberId, "Approved"),
                      disabled: workingMemberId !== null,
                    },
                    {
                      label: "Decline Request",
                      icon: <X size={14} />,
                      action: () => handleChangeStatus(memberId, "Declined"),
                      disabled: workingMemberId !== null,
                    },
                  ]
                : []),
              {
                label: "Remove Member",
                icon: <UserMinus size={14} />,
                action: () => handleRemoveMember(memberId),
                variant: "danger",
                disabled: workingMemberId !== null,
              },
            ];

            return (
              <div
                key={member.memberId}
                className="creator-owner-group-members__item"
              >
                <div className="creator-owner-group-members__avatar">
                  {name.charAt(0).toUpperCase()}
                </div>

                <div className="creator-owner-group-members__info">
                  <div className="creator-owner-group-members__identity">
                    <strong className="creator-owner-group-members__name">
                      {member.fullName}
                    </strong>

                    <span
                      className={`creator-owner-group-members__status creator-owner-group-members__status--${status}`}
                    >
                      {status}
                    </span>
                  </div>

                  <p className="creator-owner-group-members__reason">
                    {member.reasonForJoining || "No reason provided."}
                  </p>
                </div>

                <EllipsisMenu position="right" options={options} />
              </div>
            );
          })}
        </div>
      )}
    </ViewContainer>
  );
}
